import React from 'react';
import useHttp from '../../../hooks/http.hook';
import './MarketingStatsList.css';

const VISIT_TYPE_ID = 1;
const PRODUCT_TYPE_ID = 2;
const TRANSITION_TYPE_ID = 3;
const PROMO_TYPE_ID = 4;
const PUBLIC_STORE_ORIGIN = 'https://quokka.kg';

function cardName(typeId, value) {
  if (Number(typeId) === 1) return 'Количество посещений';
  const v = String(value ?? '').trim();
  return v || '—';
}

function buildTransitionUrl(customName, platform) {
  const business = String(customName ?? '').trim().replace(/^\/+/, '');
  const source = String(platform ?? '').trim();
  if (!business || !source) return '';
  return `${PUBLIC_STORE_ORIGIN}/${business}?transitionPlatform=${encodeURIComponent(source)}`;
}

async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    // fall through
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (_) {
    return false;
  }
}

function TransitionLinkBuilder({ customName }) {
  const [platform, setPlatform] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const copiedTimeoutRef = React.useRef(null);

  const url = React.useMemo(
    () => buildTransitionUrl(customName, platform),
    [customName, platform]
  );

  const onCopy = React.useCallback(async () => {
    if (!url) return;
    const ok = await copyToClipboard(url);
    if (!ok) return;
    setCopied(true);
    if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = window.setTimeout(() => {
      copiedTimeoutRef.current = null;
      setCopied(false);
    }, 2000);
  }, [url]);

  React.useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  return (
    <div className="marketing-stats-linkBuilder">
      <div className="marketing-stats-linkBuilderRow">
        <input
          id="transition-platform-input"
          type="text"
          className="marketing-stats-linkBuilderInput"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          placeholder="instagram"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          className={[
            'marketing-stats-linkBuilderCopy',
            copied ? 'marketing-stats-linkBuilderCopy--copied' : null,
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onCopy}
          disabled={!url}
          title={url || 'Введите источник'}
        >
          {copied ? 'Скопировано' : 'Копировать'}
        </button>
      </div>
      <p className="marketing-stats-linkBuilderHint">
        Введите источник, затем скопируйте ссылку и разместите её.
      </p>
      {url ? (
        <p className="marketing-stats-linkBuilderPreview" title={url}>
          {`${PUBLIC_STORE_ORIGIN}/${String(customName ?? '').trim().replace(/^\/+/, '')}?transitionPlatform=`}
          <b>{platform}</b>
        </p>
      ) : null}
    </div>
  );
}

function BarList({ rows, tone, nf, showEmpty = true }) {
  const maxCount = rows.reduce((m, row) => (row.count > m ? row.count : m), 0);
  if (rows.length === 0) {
    return showEmpty ? <p className="marketing-stats-empty">Нет данных.</p> : null;
  }
  return (
    <ul className={`marketing-stats-list marketing-stats-list--${tone}`}>
      {rows.map((row, idx) => {
        const name = cardName(row.typeId, row.value);
        const pct = maxCount > 0 ? Math.round((row.count / maxCount) * 100) : 0;
        const label = nf.format(row.count);
        return (
          <li key={`${row.typeId}:${row.value ?? ''}:${idx}`} className="marketing-stats-row">
            <span className="marketing-stats-name" title={name}>{name}</span>
            <div
              className="marketing-stats-barTrack"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={maxCount}
              aria-valuenow={row.count}
              aria-label={`${name}: ${label}`}
            >
              <div className="marketing-stats-barFill" style={{ width: `${pct}%` }} />
            </div>
            <span className="marketing-stats-qty" title={`Количество: ${label}`}>
              {label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const SKELETON_BARS = {
  transition: [78, 52, 34],
  promo: [64, 41, 22],
  product: [88, 71, 56, 40, 27],
};

function MarketingSkelRow({ width }) {
  return (
    <li className="marketing-stats-row">
      <span className="marketing-stats-skel marketing-stats-skel--name" />
      <div className="marketing-stats-barTrack">
        <div
          className="marketing-stats-barFill marketing-stats-barFill--skeleton"
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="marketing-stats-skel marketing-stats-skel--qty" />
    </li>
  );
}

function MarketingSkelCard({ rows, link = false }) {
  return (
    <div className="marketing-stats-card marketing-stats-card--skeleton" aria-hidden="true">
      <div className="marketing-stats-cardHead">
        <div className="marketing-stats-skel marketing-stats-skel--title" />
        <div className="marketing-stats-skel marketing-stats-skel--hint" />
      </div>
      {link ? (
        <div className="marketing-stats-linkBuilder">
          <div className="marketing-stats-linkBuilderRow">
            <div className="marketing-stats-skel marketing-stats-skel--input" />
            <div className="marketing-stats-skel marketing-stats-skel--button" />
          </div>
        </div>
      ) : null}
      <ul className="marketing-stats-list">
        {rows.map((width, index) => (
          <MarketingSkelRow key={index} width={width} />
        ))}
      </ul>
    </div>
  );
}

function MarketingStatsSkeleton() {
  return (
    <div className="marketing-stats-loading" aria-busy="true" aria-label="Загрузка маркетинга">
      <div className="marketing-stats-layout">
        <div className="marketing-stats-column">
          <div className="marketing-stats-visits marketing-stats-visits--skeleton" aria-hidden="true">
            <div className="marketing-stats-skel marketing-stats-skel--onDark marketing-stats-skel--visitsLabel" />
            <div className="marketing-stats-skel marketing-stats-skel--onDark marketing-stats-skel--visitsNum" />
            <div className="marketing-stats-skel marketing-stats-skel--onDark marketing-stats-skel--visitsSub" />
          </div>
          <MarketingSkelCard rows={SKELETON_BARS.transition} link />
          <MarketingSkelCard rows={SKELETON_BARS.promo} />
        </div>
        <div className="marketing-stats-column">
          <MarketingSkelCard rows={SKELETON_BARS.product} />
        </div>
      </div>
    </div>
  );
}

function MarketingStatsList({ periodId, active = false }) {
  const { requestWithMeta } = useHttp();
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  const [customName, setCustomName] = React.useState('');
  const [fetchError, setFetchError] = React.useState(false);
  const [retryTick, setRetryTick] = React.useState(0);

  const period = Number(periodId) || 1;

  React.useEffect(() => {
    if (!active) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const res = await requestWithMeta(
          `/operator/stats/marketing?periodId=${period}`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setItems([]);
          setCustomName('');
          setFetchError(true);
          return;
        }
        const raw = res?.data?.stats;
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list
          .map((row) => {
            const typeId = Number(row?.typeId ?? row?.type_id);
            const count = Number(row?.count ?? 0);
            return {
              typeId: Number.isFinite(typeId) ? typeId : null,
              value: row?.value ?? null,
              count: Number.isFinite(count) ? count : 0,
            };
          })
          .filter((row) => row.typeId != null);
        setItems(normalized);
        setCustomName(String(res?.data?.customName ?? '').trim());
        setFetchError(false);
      } catch (_) {
        if (!cancelled) {
          setItems([]);
          setCustomName('');
          setFetchError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, period, requestWithMeta, retryTick]);

  const rowsOf = React.useCallback(
    (typeId) => items.filter((row) => row.typeId === typeId),
    [items]
  );

  const visitCount = React.useMemo(
    () => rowsOf(VISIT_TYPE_ID).reduce((sum, row) => sum + row.count, 0),
    [rowsOf]
  );

  const nf = React.useMemo(() => new Intl.NumberFormat('ru-RU'), []);

  const onRetry = () => setRetryTick((t) => t + 1);

  if (!active) return null;

  return (
    <section
      id="analytics-section-marketing"
      className="marketing-stats"
      aria-label="Маркетинг"
    >
      <div className="marketing-stats-panel">
          {loading ? (
            <MarketingStatsSkeleton />
          ) : fetchError ? (
            <div className="marketing-stats-fallback">
              <p className="marketing-stats-fallbackText">Не удалось загрузить данные.</p>
              <button type="button" className="marketing-stats-retry" onClick={onRetry}>
                Повторить
              </button>
            </div>
          ) : (
            <div className="marketing-stats-layout">
              <div className="marketing-stats-column">
                <div className="marketing-stats-visits">
                  <div className="marketing-stats-visitsLabel">Количество посещений</div>
                  <div className="marketing-stats-visitsNum">{nf.format(visitCount)}</div>
                  <div className="marketing-stats-visitsSub">визитов на сайт за выбранный период</div>
                </div>
                <div className="marketing-stats-card">
                  <div className="marketing-stats-cardHead">
                    <span className="marketing-stats-cardTitle">Переходы</span>
                    <span className="marketing-stats-cardHint">откуда пришли</span>
                  </div>
                  <TransitionLinkBuilder customName={customName} />
                  <BarList rows={rowsOf(TRANSITION_TYPE_ID)} tone="transition" nf={nf} showEmpty={false} />
                </div>
                <div className="marketing-stats-card">
                  <div className="marketing-stats-cardHead">
                    <span className="marketing-stats-cardTitle">Промокоды</span>
                    <span className="marketing-stats-cardHint">использований</span>
                  </div>
                  <BarList rows={rowsOf(PROMO_TYPE_ID)} tone="promo" nf={nf} />
                </div>
              </div>
              <div className="marketing-stats-column">
                <div className="marketing-stats-card">
                  <div className="marketing-stats-cardHead">
                    <span className="marketing-stats-cardTitle">Просмотры товаров</span>
                    <span className="marketing-stats-cardHint">интерес пользователей</span>
                  </div>
                  <BarList rows={rowsOf(PRODUCT_TYPE_ID)} tone="product" nf={nf} />
                </div>
              </div>
            </div>
          )}
      </div>
    </section>
  );
}

export default MarketingStatsList;
